/**
 * The two guides the ingredient scan can match a menu against:
 *  - beef cuts: 45 cuts, 21 kinds of offal, 13 sections and 4 menu words,
 *    with ratings and the cow diagram (from the testkitchen menu-cuts prototype)
 *  - ingredients: 300 vegetables, fruit, fish and seafood with a picture,
 *    description and Japan's season (from the 72-seasons-cal app)
 */
import cutData from '../data/beefCuts.json';
import ingredientData from '../data/ingredients.json';
import { createMatcher, GuideEntry, GuideHit } from './guideMatcher';

export type GuideMode = 'cuts' | 'ingredients';

/* ── Beef cuts ── */

export interface Cut { kind: 'cut'; key: string; no: number; r: number; ja: string; ro: string; en: string; gloss: string; aka: string[]; s: number[]; use: string[]; sum: string; leg?: string }
/** `s`: ratings like a cut's, only for offal eaten as meat (タン, ハラミ, サガリ). Our estimate: TOMMY BEEF doesn't rate offal. */
export interface Offal { kind: 'offal'; key: string; id: string; g: string; ja: string; ro: string; en: string; gloss: string; aka: string[]; sum: string; s?: number[]; safety?: string; group?: { k: string; ja: string; en: string; sum: string } }
export interface Region { kind: 'region'; key: string; n: number; ja: string; ro: string; en: string; gloss: string; aka: string[]; sum: string; cuts: string[] }
export interface MenuWord { kind: 'word'; key: string; id: string; keys: string[]; html: string; ja: string; ro: string; en: string }
export type CutItem = Cut | Offal | Region | MenuWord;

interface RawCuts {
  REGIONS: Omit<Region, 'kind' | 'key' | 'cuts'>[];
  CUTS: Omit<Cut, 'kind' | 'key'>[];
  OFFAL_GROUPS: { k: string; ja: string; en: string; sum: string }[];
  OFFAL: Omit<Offal, 'kind' | 'key' | 'group'>[];
  AXES: string[][];
  MENU_WORDS: [string[], string][];
  COW: CowData;
}
export interface CowData {
  SHAPES: string[];
  TAIL: string;
  TUFT: string;
  HOOVES: number[][];
  PARTS: [number, string, string?][];
  SPOTS: [number, string][];
  OFFAL_SHAPES: Record<string, string>;
  INNER: string[];
}
const cuts = cutData as unknown as RawCuts;

export const COW = cuts.COW;
export const AXES = cuts.AXES;

export const CUT_ITEMS = new Map<string, CutItem>();
for (const r of cuts.REGIONS) {
  CUT_ITEMS.set(`r${r.n}`, { ...r, kind: 'region', key: `r${r.n}`, cuts: cuts.CUTS.filter((c) => c.r === r.n).map((c) => `c${c.no}`) });
}
for (const c of cuts.CUTS) CUT_ITEMS.set(`c${c.no}`, { ...c, kind: 'cut', key: `c${c.no}` });
for (const o of cuts.OFFAL) CUT_ITEMS.set(`o-${o.id}`, { ...o, kind: 'offal', key: `o-${o.id}`, group: cuts.OFFAL_GROUPS.find((g) => g.k === o.g) });
const WORDS: Record<string, [string, string, string]> = {
  akami: ['赤身', 'Akami', 'lean red meat'], rosu: ['ロース', 'Rōsu', 'loin'],
  karubi: ['カルビ', 'Karubi', 'kalbi, belly meat'], horumon: ['ホルモン', 'Horumon', 'offal'],
};
cuts.MENU_WORDS.forEach(([keys, html], i) => {
  const id = keys.find((k) => /^[a-z]+$/.test(k)) || `w${i}`;
  const [ja, ro, en] = WORDS[id] || [keys[0], id, ''];
  CUT_ITEMS.set(`w-${id}`, { kind: 'word', key: `w-${id}`, id, keys, html, ja, ro, en });
});

// Aliases the guide lists that a menu means otherwise: "skirt steak" on a Japanese menu is ハラミ
const NOT_ALIASES: Record<string, string[]> = { c25: ['skirt'] };

// Rank: lower wins when one name points at several things. Menu words first
// (カルビ), then a cut or organ's own name, then a section's own name, then aliases.
const cutEntries: GuideEntry[] = [];
for (const it of CUT_ITEMS.values()) {
  if (it.kind === 'word') { it.keys.forEach((k) => cutEntries.push({ raw: k, key: it.key, rank: -1 })); continue; }
  const own = it.kind === 'region' ? 1 : 0, alias = it.kind === 'region' ? 3 : 2;
  cutEntries.push({ raw: it.ja, key: it.key, rank: own }, { raw: it.ro, key: it.key, rank: own });
  (it.aka || []).filter((a) => !NOT_ALIASES[it.key]?.includes(a)).forEach((a) => cutEntries.push({ raw: a, key: it.key, rank: alias }));
  if (it.kind !== 'region' && it.en) cutEntries.push({ raw: it.en.replace(/\s*\(.*$/, ''), key: it.key, rank: alias });
}
// Menu spellings the guide doesn't list
for (const [t, key] of [['カタロース', 'c10'], ['ウチモモ', 'r9'], ['ソトモモ', 'r10']]) cutEntries.push({ raw: t, key, rank: 2 });

export const findCuts = createMatcher({
  entries: cutEntries,
  // Aliases that do more harm than good on a real menu: too short, or an everyday word
  exclude: ['くり', 'まる', 'らむ', 'かん', 'たけ', 'たけのこ', 'ささみ', 'はーと', 'ひっぷ', 'けつ', 'おかま', '白肉', '軟骨', '唐辛子', '千葉', 'ばいぶる', '天肉', '韓国語', 'ひも', 'ほそ', 'ねじ', 'つら', '肩肉'],
  stop: ['ろーすと', 'わんたん', 'たんめん', 'たんたん', 'たんぶらー', 'たんどりー', 'すたんど', 'ぼたん', 'くりーむ', 'ふかひれ', 'ひれざけ', 'ひれ酒', 'らむね', 'らむこーく', 'まるげりーた', 'はつらつ', 'はつもの', 'みのり', 'ごまめ', 'ぐらんぷり'],
  okAfter: ['すてーき', 'かつ', 'さんど', 'しちゅー', 'ゆっけ', 'そてー', 'すらいす', 'ぽんず', 'ぽん酢', 'もと', 'さき', 'なか', 'した'],
  okBefore: ['ねぎ', 'ねぎしお', 'あつぎり', 'みっくす', 'にんにく', 'がーりっく'],
  // 'short rib' is how menus translate カルビ, not the mid belly the guide files it under
  latinExclude: ['arm', 'shoulder', 'belly', 'plate', 'short rib', 'round', 'heel', 'shin', 'tail', 'chuck', 'neck', 'tripe', 'loin', 'cartilage', 'diaphragm',
    'pillow', 'spear', 'thousand', 'necktie', 'little turtle', 'big round', 'small round', 'grandchild round', 'middle meat', 'upper arm', 'upper misuji',
    'small triangle', 'belly head', 'rib fingers', 'loin end', 'loin eye', 'knuckle core', 'knuckle cover', 'fore shank', 'hind shank', 'rump heart',
    'tenderloin tail', 'tenderloin head', 'rib cap'],
});

/** Is this line about another animal? Then the beef notes are only a guide. */
export const ANIMALS: [string, string, RegExp][] = [
  ['pork', '豚 (pork)', /豚|ポーク|pork|やきとん|焼きとん|焼とん/i],
  ['chicken', '鶏 (chicken)', /鶏|鳥|チキン|chicken|やきとり|焼き鳥|焼鳥/i],
  ['lamb', '羊 (lamb)', /羊|マトン|ジンギスカン|lamb|mutton|ラム肉/i],
];
const BEEF = /牛|ビーフ|beef|和牛|黒毛/i;
export const animalOf = (text: string) => (BEEF.test(text) ? 'beef' : ANIMALS.find(([, , re]) => re.test(text))?.[0] || null);

/** Diaphragm and tongue: offal on paper, but a yakiniku menu sells them with the meat. */
export const MEAT_OFFAL = ['harami', 'sagari', 'tan'];

/** Offal the guide rates and shows as meat: タン, ハラミ and サガリ. */
export const eatenAsMeat = (it: CutItem): it is Offal & { s: number[] } => it.kind === 'offal' && !!it.s;

/** Does the line name beef by itself: 牛 / beef, or a cut, section or diaphragm (not a word like ロース, which pork has too)? */
const namesBeef = (text: string) => {
  const animal = animalOf(text);
  if (animal) return animal === 'beef';
  return findCuts(text).some((h) => h.keys.some((k) => {
    const it = CUT_ITEMS.get(k)!;
    return it.kind === 'cut' || it.kind === 'region' || (it.kind === 'offal' && MEAT_OFFAL.includes(it.id));
  }));
};

/**
 * The animal a whole menu is about, when it isn't beef: one named on at least
 * two lines, and on more lines than beef is. A yakiniku menu with a pork set or
 * a chicken dish is still a beef menu.
 */
export function menuAnimalOf(texts: string[]): string | null {
  const beef = texts.filter(namesBeef).length;
  const [top] = ANIMALS.map(([k, , re]) => [k, texts.filter((t) => re.test(t)).length] as const).sort((a, b) => b[1] - a[1]);
  return top && top[1] >= 2 && top[1] > beef ? top[0] : null;
}

/**
 * The sections a menu word means, as its note says. Set by hand: matching cut
 * names would light the tenderloin for ロース ("butt tenderloin" has "loin" in it).
 * 赤身 is any lean section; ホルモン is offal, which has no section.
 */
const WORD_REGIONS: Record<string, number[]> = { rosu: [4, 3, 2], karubi: [5, 7, 8], akami: [1, 9, 10, 11, 12] };
export const regionsOfWord = (w: MenuWord): number[] => WORD_REGIONS[w.id] || [];

const cutsIn = (regions: number[]) => [...CUT_ITEMS.values()].filter((it): it is Cut => it.kind === 'cut' && regions.includes(it.r));

/** Each rating's lowest and highest across the cuts in these sections. */
export function ratingRanges(regions: number[]): { cuts: number; ranges: [number, number][] } {
  const inArea = cutsIn(regions);
  if (!inArea.length) return { cuts: 0, ranges: [] };
  return { cuts: inArea.length, ranges: AXES.map((_, k) => [Math.min(...inArea.map((c) => c.s[k])), Math.max(...inArea.map((c) => c.s[k]))]) };
}

/**
 * A section or menu word stands for a whole area: per rating, the typical value
 * (median) and the highest across the cuts in it. The median, not the lowest:
 * one odd cut (ネック) shouldn't make ロース read as lean.
 */
export function areaRatings(regions: number[]): { cuts: number; typical: number[]; top: number[] } | null {
  const inArea = cutsIn(regions);
  if (!inArea.length) return null;
  const median = (v: number[]) => {
    const s = [...v].sort((a, b) => a - b), m = s.length >> 1;
    return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
  };
  const axis = (k: number) => inArea.map((c) => c.s[k]);
  return {
    cuts: inArea.length,
    typical: AXES.map((_, k) => median(axis(k))),
    top: AXES.map((_, k) => Math.max(...axis(k))),
  };
}

/* ── Ingredients ── */

export interface Ingredient {
  id: string;
  en: string;
  /** Kanji, or the English/romaji where the data has no kanji */
  ja: string;
  kana: string;
  kind: string;
  sub: string;
  desc: string;
  /** Japanese note on the season */
  note: string;
  /** File in /scan-ingredients, or '' */
  img: string;
  /** Months in season in Japan */
  months: number[];
}

export const INGREDIENTS = new Map<string, Ingredient>((ingredientData as Ingredient[]).map((i) => [i.id, i]));

const ingredientEntries: GuideEntry[] = [];
for (const i of INGREDIENTS.values()) {
  ingredientEntries.push({ raw: i.ja, key: i.id, rank: 0 }, { raw: i.kana, key: i.id, rank: 0 }, { raw: i.en, key: i.id, rank: 1 });
}

export const findIngredients = createMatcher({
  entries: ingredientEntries,
  singleWhole: true,
  guardMax: 2,
  // Everyday words that are also an ingredient's kana: 梨 / 鯛 / 牡蠣 / 桃 are matched by their kanji
  exclude: ['なし', 'たい', 'かき', 'もも', 'さけ'],
  okAfter: ['ぽんず', 'さらだ', 'てんぷら', 'いり', 'ぞえ', 'あえ'],
  minLatin: 4,
});

export type { GuideHit };

/** Beef cuts and ingredients together: every name on the line, in reading order. */
export const findAll = (text: string): GuideHit[] =>
  [...findCuts(text), ...findIngredients(text)].sort((a, b) => a.start - b.start || b.end - a.end);

const LETTER = new RegExp('\\p{L}', 'u');

/**
 * Do the matched names leave part of the line unexplained? (たたききゅうり: きゅうり
 * matched, たたき didn't.) Prices and symbols don't count.
 */
export function isPartialMatch(text: string, hits: GuideHit[]): boolean {
  const chars = Array.from(text.normalize('NFKC'));
  hits.forEach((h) => { for (let i = h.start; i < h.end; i++) chars[i] = ' '; });
  const rest = chars.join('')
    .replace(/[(（]?税[込抜別](価格)?[)）]?/g, ' ')
    .replace(/[¥$]\s*[\d,]+/g, ' ')
    .replace(/[\d,]+\s*(円|yen)?/gi, ' ');
  return LETTER.test(rest);
}
